import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchDepot,
  fetchPositionWerte,
  addPosition,
  removePosition,
  addTransaktion,
  setGruppe,
  createGruppe,
  renameGruppe,
  deleteGruppe,
  reorderGruppen,
  selectDepotPositionen,
  selectDepotGruppen,
  selectDepotStatus,
} from 'modules/aktien/features/depotSlice';
import { selectAktie } from 'modules/aktien/features/aktienDbSlice';
import {
  formatKurs,
  formatProz,
  formatVeraenderung,
  formatDatum,
  formatZahl,
} from 'core/utils/format';
import UrteilZelle from 'modules/aktien/components/UrteilZelle';

const inputKlasse =
  'w-full rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink placeholder-ink-muted focus:border-ink/30 focus:outline-none focus:ring-2 focus:ring-ink/15';

const leerePosition = { kennung: '', kaufdatum: '', kaufkurs: '', stueckzahl: '' };

// Wie viele Positionswerte gleichzeitig geladen werden (schont das OnVista-Rate-Limit).
const MAX_PARALLEL_WERTE = 3;

/** Optik je History-Abruf-Status. */
const HISTORY_META = {
  PENDING: { label: 'Wartet', klasse: 'bg-paper-sunken text-ink-muted ring-line' },
  RUNNING: { label: 'Lädt…', klasse: 'bg-brand-500/10 text-brand-600 ring-brand-500/20 animate-pulse' },
  DONE: { label: 'Vollständig', klasse: 'bg-gain/10 text-gain ring-gain/20' },
  ERROR: { label: 'Fehler', klasse: 'bg-loss/10 text-loss ring-loss/20' },
};

/** Vorzeichen-Farbe fuer Gewinn-/Performance-Werte. */
const tonKlasse = (v) =>
  v == null ? 'text-ink-muted' : Number(v) >= 0 ? 'text-gain' : 'text-loss';

/**
 * Reiter „Depot": Positionen als Karten, organisiert in selbst angelegten Gruppen
 * (Drag&Drop). Pro Position: Kauf-Aggregat, aktueller Wert/Gewinn, Dividende,
 * 30-/200-Tage-Entwicklung, Gesamturteil sowie Buttons für Detail, Analyse und
 * Verändern (Nachkauf/Verkauf). Während die Tiefen-History lädt, wird nachgeladen.
 */
export default function DepotView() {
  const dispatch = useDispatch();
  const positionen = useSelector(selectDepotPositionen);
  const gruppen = useSelector(selectDepotGruppen);
  const status = useSelector(selectDepotStatus);
  const [eingabe, setEingabe] = useState(leerePosition);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    dispatch(fetchDepot());
  }, [dispatch]);

  // Werte nach und nach nachladen: je fertiger Position rückt die nächste nach,
  // maximal MAX_PARALLEL_WERTE gleichzeitig.
  useEffect(() => {
    const ladend = positionen.filter((p) => p.werteStatus === 'loading').length;
    const frei = MAX_PARALLEL_WERTE - ladend;
    if (frei <= 0) return;
    positionen
      .filter((p) => (p.werteStatus ?? 'idle') === 'idle')
      .slice(0, frei)
      .forEach((p) => dispatch(fetchPositionWerte(p.isin)));
  }, [positionen, dispatch]);

  // Solange die Tiefen-History einer Position lädt, deren Werte regelmäßig nachladen.
  const historyIsins = useMemo(
    () => positionen
      .filter((p) => p.historyStatus === 'PENDING' || p.historyStatus === 'RUNNING')
      .map((p) => p.isin)
      .join(','),
    [positionen]
  );
  useEffect(() => {
    if (!historyIsins) return undefined;
    const id = setInterval(
      () => historyIsins.split(',').forEach((isin) => dispatch(fetchPositionWerte(isin))),
      5000
    );
    return () => clearInterval(id);
  }, [historyIsins, dispatch]);

  const handleChange = (feld) => (e) =>
    setEingabe((prev) => ({ ...prev, [feld]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFehler(null);
    if (!eingabe.kennung.trim()) return;
    try {
      await dispatch(
        addPosition({
          kennung: eingabe.kennung.trim(),
          kaufdatum: eingabe.kaufdatum || null,
          kaufkurs: eingabe.kaufkurs === '' ? null : Number(eingabe.kaufkurs),
          stueckzahl: eingabe.stueckzahl === '' ? null : Number(eingabe.stueckzahl),
        })
      ).unwrap();
      setEingabe(leerePosition);
    } catch (err) {
      setFehler('Position konnte nicht hinzugefügt werden. Ist die Aktie bereits importiert?');
    }
  };

  // Positionen nach Gruppe bündeln: je Gruppe + „Ohne Gruppe".
  const sektionen = useMemo(() => baueSektionen(positionen, gruppen), [positionen, gruppen]);

  // Kennzahlen für die Werte-Anzeige: Gesamtwert, größter Gruppenwert, Währung.
  const gesamtWert = useMemo(() => summeWert(positionen), [positionen]);
  const maxWert = useMemo(() => Math.max(0, ...sektionen.map((s) => s.wert)), [sektionen]);
  const waehrung = useMemo(() => depotWaehrung(positionen), [positionen]);

  const handleDrop = (gruppeUuid) => (isin) => dispatch(setGruppe({ isin, gruppeUuid }));

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Depot</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Eigene Positionen mit Kaufdaten – gruppiert, mit Wert, Gewinn, Dividende und Urteil.
        </p>
      </header>

      <DepotForm eingabe={eingabe} onChange={handleChange} onSubmit={handleSubmit} fehler={fehler} />

      <GruppenLeiste
        gruppen={gruppen}
        onCreate={(name) => dispatch(createGruppe(name))}
      />

      {status === 'loading' && positionen.length === 0 && (
        <p className="text-ink-muted">Lade Depot…</p>
      )}

      {positionen.length === 0 && status !== 'loading' ? (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch keine Positionen. Füge oben eine Aktie über Name, ISIN oder WKN hinzu.
        </p>
      ) : (
        <div className="space-y-6">
          <GesamtwertBanner wert={gesamtWert} waehrung={waehrung} />
          {sektionen.map((s, i) => (
            <GruppeSektion
              key={s.uuid || 'ohne'}
              sektion={s}
              prozentVomMax={maxWert > 0 ? (s.wert / maxWert) * 100 : 0}
              waehrung={waehrung}
              istErste={i === 0}
              istLetzte={i === sektionen.length - 1}
              onDrop={handleDrop(s.uuid)}
              onRename={(name) => dispatch(renameGruppe({ uuid: s.uuid, name }))}
              onDelete={() => dispatch(deleteGruppe(s.uuid))}
              onMove={(richtung) => dispatch(reorderGruppen(verschiebe(gruppen, s.uuid, richtung)))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Summiert die aktuellen Werte einer Positionsliste (fehlende Werte zählen als 0). */
function summeWert(liste) {
  return liste.reduce((acc, p) => acc + (Number(p.aktuellerWert) || 0), 0);
}

/** Bündelt Positionen je Gruppe (in Gruppen-Reihenfolge) plus „Ohne Gruppe" am Ende. */
function baueSektionen(positionen, gruppen) {
  const sektionen = gruppen.map((g) => {
    const pos = positionen.filter((p) => p.gruppeUuid === g.uuid);
    return { uuid: g.uuid, name: g.name, positionen: pos, wert: summeWert(pos) };
  });
  const ohne = positionen.filter((p) => !p.gruppeUuid);
  sektionen.push({ uuid: null, name: 'Ohne Gruppe', positionen: ohne, wert: summeWert(ohne) });
  return sektionen;
}

/** Einheitliche Anzeige-Währung (i. d. R. EUR), falls alle Positionen dieselbe nutzen. */
function depotWaehrung(positionen) {
  const waehrungen = new Set(positionen.map((p) => p.anzeigeWaehrung || p.currency).filter(Boolean));
  return waehrungen.size === 1 ? [...waehrungen][0] : undefined;
}

/** Auffällige Anzeige des Depot-Gesamtwerts über allen Gruppen. */
function GesamtwertBanner({ wert, waehrung }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-line bg-paper-soft px-5 py-4">
      <span className="font-mono text-xs uppercase tracking-wider text-ink-muted">Gesamtwert</span>
      <span className="font-serif text-2xl font-semibold tabular-nums text-ink">
        {formatKurs(wert, waehrung)}
      </span>
    </div>
  );
}

/** Liefert die neue UUID-Reihenfolge nach Verschieben einer Gruppe um eine Position. */
function verschiebe(gruppen, uuid, richtung) {
  const uuids = gruppen.map((g) => g.uuid);
  const i = uuids.indexOf(uuid);
  const j = richtung === 'hoch' ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= uuids.length) return uuids;
  [uuids[i], uuids[j]] = [uuids[j], uuids[i]];
  return uuids;
}

/** Eingabeformular für eine neue Position. */
function DepotForm({ eingabe, onChange, onSubmit, fehler }) {
  return (
    <form onSubmit={onSubmit} className="mb-6 rounded-xl border border-line bg-paper-soft p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Feld label="Name / ISIN / WKN">
          <input type="text" value={eingabe.kennung} onChange={onChange('kennung')}
            placeholder="z. B. SAP oder DE0007164600" className={inputKlasse} />
        </Feld>
        <Feld label="Kaufdatum">
          <input type="date" value={eingabe.kaufdatum} onChange={onChange('kaufdatum')} className={inputKlasse} />
        </Feld>
        <Feld label="Kaufkurs">
          <input type="number" step="0.0001" min="0" value={eingabe.kaufkurs}
            onChange={onChange('kaufkurs')} placeholder="z. B. 120,50" className={inputKlasse} />
        </Feld>
        <Feld label="Stückzahl">
          <input type="number" step="0.0001" min="0" value={eingabe.stueckzahl}
            onChange={onChange('stueckzahl')} placeholder="z. B. 10" className={inputKlasse} />
        </Feld>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button type="submit"
          className="rounded-lg bg-ink px-4 py-2 text-sm font-medium text-paper transition-colors hover:bg-ink-soft">
          Position hinzufügen
        </button>
        {fehler && <span className="text-sm text-loss">{fehler}</span>}
      </div>
    </form>
  );
}

/** Beschriftetes Eingabefeld. */
function Feld({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

/** Leiste zum Anlegen neuer Gruppen. */
function GruppenLeiste({ onCreate }) {
  const [name, setName] = useState('');
  const handle = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onCreate(name.trim());
    setName('');
  };
  return (
    <form onSubmit={handle} className="mb-6 flex gap-2">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Neue Gruppe (z. B. Dividendenaktien)…"
        className="w-full max-w-xs rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink placeholder-ink-muted focus:border-ink/30 focus:outline-none focus:ring-2 focus:ring-ink/15" />
      <button type="submit"
        className="rounded-lg border border-line bg-paper-soft px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-ink/5">
        Gruppe anlegen
      </button>
    </form>
  );
}

/** Ein Abschnitt (Gruppe oder „Ohne Gruppe") als Drop-Zone mit Positionskarten. */
function GruppeSektion({ sektion, prozentVomMax, waehrung, istErste, istLetzte, onDrop, onRename, onDelete, onMove }) {
  const [ueber, setUeber] = useState(false);
  const istEchteGruppe = Boolean(sektion.uuid);

  const handleDrop = (e) => {
    e.preventDefault();
    setUeber(false);
    const isin = e.dataTransfer.getData('text/plain');
    if (isin) onDrop(isin);
  };

  return (
    <section
      onDragOver={(e) => { e.preventDefault(); setUeber(true); }}
      onDragLeave={() => setUeber(false)}
      onDrop={handleDrop}
      className={`rounded-xl border p-4 transition-colors ${
        ueber ? 'border-brand-400 bg-brand-50/40' : 'border-line bg-paper/40'
      }`}
    >
      <SektionKopf
        sektion={sektion} istEchteGruppe={istEchteGruppe}
        prozentVomMax={prozentVomMax} waehrung={waehrung}
        istErste={istErste} istLetzte={istLetzte}
        onRename={onRename} onDelete={onDelete} onMove={onMove}
      />
      {sektion.positionen.length === 0 ? (
        <p className="py-3 text-center text-xs text-ink-muted">Karten hierher ziehen…</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {sektion.positionen.map((p) => (
            <PositionsKarte key={p.isin} p={p} />
          ))}
        </div>
      )}
    </section>
  );
}

/** Kopfzeile eines Abschnitts: Name, Wert/Anteil, Umbenennen/Löschen, Sortier-Pfeile. */
function SektionKopf({ sektion, istEchteGruppe, prozentVomMax, waehrung, istErste, istLetzte, onRename, onDelete, onMove }) {
  const [bearbeite, setBearbeite] = useState(false);
  const [name, setName] = useState(sektion.name);

  const speichern = () => {
    if (name.trim() && name.trim() !== sektion.name) onRename(name.trim());
    setBearbeite(false);
  };

  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        {bearbeite ? (
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
            onBlur={speichern} onKeyDown={(e) => e.key === 'Enter' && speichern()}
            className="rounded-md border border-line bg-paper px-2 py-1 text-sm text-ink" />
        ) : (
          <h2 className="font-serif text-lg font-semibold text-ink">{sektion.name}</h2>
        )}
        <span className="font-mono text-xs text-ink-muted">{sektion.positionen.length}</span>
        <div className="ml-auto flex items-center gap-3">
          <GruppenWert wert={sektion.wert} prozent={prozentVomMax} waehrung={waehrung} />
          {istEchteGruppe && (
            <div className="flex items-center gap-1 text-ink-muted">
              <IconBtn title="nach oben" disabled={istErste} onClick={() => onMove('hoch')}>▲</IconBtn>
              <IconBtn title="nach unten" disabled={istLetzte} onClick={() => onMove('runter')}>▼</IconBtn>
              <IconBtn title="umbenennen" onClick={() => { setName(sektion.name); setBearbeite(true); }}>✎</IconBtn>
              <IconBtn title="Gruppe löschen" onClick={onDelete} gefahr>🗑</IconBtn>
            </div>
          )}
        </div>
      </div>
      <Wertbalken prozent={prozentVomMax} />
    </div>
  );
}

/** Zeigt den Gruppenwert und seinen Anteil am größten Gruppenwert (Maximum). */
function GruppenWert({ wert, prozent, waehrung }) {
  return (
    <div className="text-right leading-tight">
      <div className="font-mono text-sm font-semibold tabular-nums text-ink">{formatKurs(wert, waehrung)}</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
        {Math.round(prozent)} % vom Max
      </div>
    </div>
  );
}

/** Schmaler Balken: Anteil dieser Gruppe am größten Gruppenwert. */
function Wertbalken({ prozent }) {
  return (
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-paper-sunken">
      <div
        className="h-full rounded-full bg-ink/60 transition-all"
        style={{ width: `${Math.max(0, Math.min(100, prozent))}%` }}
      />
    </div>
  );
}

/** Kleiner Icon-Button für die Gruppen-Aktionen. */
function IconBtn({ children, title, onClick, disabled, gefahr }) {
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled}
      className={`rounded-md px-1.5 py-0.5 text-sm transition-colors disabled:opacity-30 ${
        gefahr ? 'hover:bg-loss/10 hover:text-loss' : 'hover:bg-ink/5 hover:text-ink'
      }`}>
      {children}
    </button>
  );
}

/** Eine Positionskarte mit allen Kennzahlen, Aktionen und ausklappbarem Verändern-Dialog. */
function PositionsKarte({ p }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(false);

  const oeffne = (view) => {
    dispatch(selectAktie(p.isin));
    navigate(`/projekte/aktienanalyse/${view}/${p.isin}`);
  };

  const onDragStart = (e) => e.dataTransfer.setData('text/plain', p.isin);
  const hist = HISTORY_META[p.historyStatus] || HISTORY_META.PENDING;
  // Anzeige-Währung für alle in Euro umgerechneten Geldwerte der Karte.
  const w = p.anzeigeWaehrung || p.currency;
  // Solange die angereicherten Werte noch nachgeladen werden: Platzhalter zeigen.
  const laedt = p.werteStatus === 'idle' || p.werteStatus === 'loading';

  return (
    <div draggable onDragStart={onDragStart}
      className="cursor-grab rounded-xl border border-line bg-paper-soft p-4 active:cursor-grabbing">
      {/* Kopf: Name + Urteil + History */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-serif text-base font-semibold text-ink">{p.aktienName || p.isin}</div>
          <div className="font-mono text-xs text-ink-muted">
            {p.isin}{p.wkn ? ` · WKN ${p.wkn}` : ''}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {laedt ? (
            <span className="h-5 w-9 animate-pulse rounded-full bg-paper-sunken" />
          ) : (
            <UrteilZelle ampel={p.gesamtUrteilAmpel} score={p.gesamtUrteilScore} />
          )}
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[11px] font-medium ring-1 ${hist.klasse}`}
            title="Abruf der kompletten Kurshistorie">
            {hist.label}
          </span>
        </div>
      </div>

      {/* Kennzahlen-Raster */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
        <Wert label="Akt. Kurs" laedt={laedt} value={formatKurs(p.aktuellPreis, w)}
          sub={formatProz(p.aenderungProz)} subTon={tonKlasse(p.aenderungProz)} />
        <Wert label="Wert" laedt={laedt} value={formatKurs(p.aktuellerWert, w)} />
        <Wert label="Gewinn" laedt={laedt} value={p.gewinnAbsolut == null ? '–' : formatVeraenderung(p.gewinnAbsolut, w)}
          valueTon={tonKlasse(p.gewinnAbsolut)} sub={formatProz(p.gewinnProz)} subTon={tonKlasse(p.gewinnProz)} />
        <Wert label="Bestand" value={p.stueckzahl != null ? `${formatZahl(p.stueckzahl)} Stk.` : '–'}
          sub={p.durchschnittskaufkurs != null ? `Ø ${formatKurs(p.durchschnittskaufkurs, w)}` : null} />
        <Wert label="Perf 30 T" laedt={laedt} value={formatProz(p.perf30Tage)} valueTon={tonKlasse(p.perf30Tage)} />
        <Wert label="Perf 200 T" laedt={laedt} value={formatProz(p.perf200Tage)} valueTon={tonKlasse(p.perf200Tage)} />
        <Wert label="Dividende" laedt={laedt}
          value={p.dividendeBetrag != null ? formatKurs(p.dividendeBetrag, w) : '–'}
          sub={p.dividendeRendite != null ? `${formatZahl(p.dividendeRendite)} %` : null} />
        <Wert label="Nächster Ex-Tag" laedt={laedt} value={formatDatum(p.naechsterExTag)}
          sub={p.naechsterZahltag ? `Zahltag ${formatDatum(p.naechsterZahltag)}` : null} />
        <Wert label="Realisiert"
          value={p.realisierterGewinn && Number(p.realisierterGewinn) !== 0
            ? formatVeraenderung(p.realisierterGewinn, w) : '–'}
          valueTon={tonKlasse(p.realisierterGewinn)} />
      </div>

      {/* Aktionen */}
      <div className="mt-4 flex flex-wrap gap-2">
        <KartenBtn onClick={() => oeffne('aktie')}>📊 Detail</KartenBtn>
        <KartenBtn onClick={() => oeffne('analyse')}>🔬 Analyse</KartenBtn>
        <KartenBtn onClick={() => setDialog((o) => !o)} aktiv={dialog}>✎ Verändern</KartenBtn>
      </div>

      {dialog && (
        <VeraendernDialog
          currency={p.currency}
          onTransaktion={(t) => dispatch(addTransaktion({ isin: p.isin, transaktion: t }))}
          onEntfernen={() => dispatch(removePosition(p.isin))}
          onClose={() => setDialog(false)}
        />
      )}
    </div>
  );
}

/** Eine Kennzahl in der Karte (Label, Wert, optionaler Subtext). Zeigt bei {@code laedt} Platzhalter. */
function Wert({ label, value, valueTon = 'text-ink', sub, subTon = 'text-ink-muted', laedt = false }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</div>
      {laedt ? (
        <div className="mt-1 h-4 w-14 animate-pulse rounded bg-paper-sunken" />
      ) : (
        <>
          <div className={`font-mono tabular-nums ${valueTon}`}>{value ?? '–'}</div>
          {sub && <div className={`font-mono text-xs tabular-nums ${subTon}`}>{sub}</div>}
        </>
      )}
    </div>
  );
}

/** Karten-Aktionsbutton. */
function KartenBtn({ children, onClick, aktiv }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
        aktiv ? 'border-ink bg-ink text-paper' : 'border-line bg-paper text-ink-muted hover:bg-ink/5 hover:text-ink'
      }`}>
      {children}
    </button>
  );
}

const leereTransaktion = { typ: 'KAUF', menge: '', kurs: '', datum: '' };

/** Ein-/ausklappbarer Dialog zum Nachkaufen/Verkaufen oder Entfernen einer Position. */
function VeraendernDialog({ currency, onTransaktion, onEntfernen, onClose }) {
  const [eingabe, setEingabe] = useState(leereTransaktion);
  const setFeld = (feld) => (e) => setEingabe((prev) => ({ ...prev, [feld]: e.target.value }));

  const speichern = (e) => {
    e.preventDefault();
    if (eingabe.menge === '' || Number(eingabe.menge) <= 0) return;
    onTransaktion({
      typ: eingabe.typ,
      menge: Number(eingabe.menge),
      kurs: eingabe.kurs === '' ? null : Number(eingabe.kurs),
      datum: eingabe.datum || null,
    });
    setEingabe(leereTransaktion);
    onClose();
  };

  return (
    <form onSubmit={speichern} className="mt-4 rounded-lg border border-line bg-paper p-3">
      <div className="grid gap-2 sm:grid-cols-4">
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-ink-muted">Art</span>
          <select value={eingabe.typ} onChange={setFeld('typ')} className={inputKlasse}>
            <option value="KAUF">Nachkauf</option>
            <option value="VERKAUF">Verkauf</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-ink-muted">Stückzahl</span>
          <input type="number" step="0.0001" min="0" value={eingabe.menge} onChange={setFeld('menge')} className={inputKlasse} />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-ink-muted">
            Kurs{currency ? ` (${currency})` : ''}
          </span>
          <input type="number" step="0.0001" min="0" value={eingabe.kurs} onChange={setFeld('kurs')} className={inputKlasse} />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-ink-muted">Datum</span>
          <input type="date" value={eingabe.datum} onChange={setFeld('datum')} className={inputKlasse} />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button type="submit"
          className="rounded-lg bg-ink px-3 py-1.5 text-sm font-medium text-paper transition-colors hover:bg-ink-soft">
          Speichern
        </button>
        <button type="button" onClick={onEntfernen}
          className="rounded-lg px-2 py-1.5 text-xs text-ink-muted transition-colors hover:bg-loss/10 hover:text-loss">
          Position ganz entfernen
        </button>
      </div>
    </form>
  );
}
